"use client";
import Image from "next/image";
import logo from "@public/logo.svg";
import { JSX, useState } from "react";
import { Reactions } from "./Reactions";
import { CallDurationBadge, BreakoutToolbarTimers } from "./CallDurationBadge";
import { useTranslations } from "next-intl";
import {
  X,
  Mic,
  Info,
  Users,
  Grid2x2,
  Video,
  MicOff,
  Monitor,
  FileText,
  PhoneOff,
  Settings,
  VideoOff,
  BarChart3,
  Paintbrush,
  MessageCircle,
  MoreHorizontal
} from "lucide-react";

/** Props for the MediaControls component */
interface MediaControlsProps {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenSharing: boolean;
  isSomeoneElseSharing?: boolean;
  isHandRaised: boolean;
  unreadMessages: number;
  unreadPolls: boolean;
  unreadNotes: boolean;
  hasUnreadJoinRequests?: boolean;
  raisedHandsCount: number;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onToggleScreenShare: () => void;
  onToggleChat: () => void;
  showChatButton?: boolean;
  onToggleParticipants: () => void;
  onTogglePolls: () => void;
  onToggleWhiteboard: () => void;
  onToggleNotes: () => void;
  onToggleSettings: () => void;
  onToggleMeetingDetails: () => void;
  showBreakoutControl?: boolean;
  isInBreakout?: boolean;
  onToggleBreakouts?: () => void;
  onReturnToMainMeeting?: () => void;
  breakoutTimerRemainingSec?: number | null;
  breakoutCloseCountdownSec?: number | null;
  onToggleHandRaise: () => void;
  onSendReaction: (emoji: string) => void;
  onLeave: () => void;
}

/** MediaControls - Bottom toolbar for media/interaction toggles */
export const MediaControls = ({
  isMicEnabled,
  isCameraEnabled,
  isScreenSharing,
  isSomeoneElseSharing,
  isHandRaised,
  unreadMessages,
  unreadPolls,
  unreadNotes,
  hasUnreadJoinRequests,
  raisedHandsCount,
  onToggleMic,
  onToggleCamera,
  onToggleScreenShare,
  onToggleChat,
  showChatButton = true,
  onToggleParticipants,
  onTogglePolls,
  onToggleWhiteboard,
  onToggleNotes,
  onToggleSettings,
  onToggleMeetingDetails,
  showBreakoutControl = false,
  isInBreakout = false,
  onToggleBreakouts = () => {},
  onReturnToMainMeeting = () => {},
  breakoutTimerRemainingSec = null,
  breakoutCloseCountdownSec = null,
  onToggleHandRaise,
  onSendReaction,
  onLeave
}: MediaControlsProps): JSX.Element => {
  const t = useTranslations();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toolbarFocusRing =
    "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-white";
  const toolbarInteractive = `cursor-pointer border transition-all duration-200 active:scale-[0.97] ${toolbarFocusRing}`;

  const controlButtonClass = (isActive: boolean) =>
    `relative p-3 sm:p-3.5 rounded-full ${toolbarInteractive} ${
      isActive
        ? "border-primary/80 bg-primary text-white shadow-lg hover:border-primary hover:bg-primary-light hover:shadow-xl hover:ring-2 hover:ring-primary/25"
        : "border-error/80 bg-error text-white shadow-md hover:border-white/40 hover:bg-error-light hover:shadow-lg hover:ring-2 hover:ring-error/25"
    }`;

  const secondaryButtonClass = `relative p-3 sm:p-3.5 rounded-full bg-white text-gray-600 shadow-md ${toolbarInteractive} border-gray-200/70 hover:border-primary hover:bg-gray-50 hover:text-gray-800 hover:shadow-lg hover:ring-2 hover:ring-primary/15`;

  const mobileMenuButtonClass = `relative flex flex-col items-center gap-1 p-3 rounded-xl bg-gray-50 text-gray-700 ${toolbarInteractive} border-gray-200/80 hover:border-primary hover:bg-gray-100 hover:ring-2 hover:ring-primary/10`;

  const iconClass = "w-5 h-5 sm:w-6 sm:h-6";

  return (
    <nav
      className="shrink-0 px-2 py-2 sm:px-4 sm:py-3 md:px-6 md:py-4 flex justify-center"
      role="toolbar"
      aria-label={t("controls.aria.mediaControls")}
    >
      <div className="inline-flex items-center gap-1.5 sm:gap-2 md:gap-3 px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-3 bg-white/98 backdrop-blur-lg rounded-xl sm:rounded-2xl shadow-lg border border-gray-200">
        {/* Logo on the left */}
        <div className="hidden sm:flex items-center ltr:mr-2 rtl:ml-2">
          <Image
            src={logo}
            alt={t("common.appName")}
            width={120}
            height={15}
            style={{ height: "auto" }}
            className="opacity-100"
            priority
          />
        </div>

        {/* Center controls group */}
        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
          {/* Primary Controls Group */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Mic control */}
            <button
              onClick={onToggleMic}
              className={controlButtonClass(isMicEnabled)}
              aria-label={isMicEnabled ? t("participant.mute") : t("participant.unmute")}
              aria-pressed={!isMicEnabled}
              title={isMicEnabled ? t("participant.mute") : t("participant.unmute")}
            >
              {isMicEnabled ? <Mic className={iconClass} /> : <MicOff className={iconClass} />}
            </button>

            {/* Camera control */}
            <button
              onClick={onToggleCamera}
              className={controlButtonClass(isCameraEnabled)}
              aria-label={isCameraEnabled ? t("settings.cameraOff") : t("settings.cameraOn")}
              aria-pressed={isCameraEnabled}
              title={isCameraEnabled ? t("settings.cameraOff") : t("settings.cameraOn")}
            >
              {isCameraEnabled ? <Video className={iconClass} /> : <VideoOff className={iconClass} />}
            </button>

            {/* Screen share */}
            <button
              onClick={isSomeoneElseSharing ? undefined : onToggleScreenShare}
              disabled={isSomeoneElseSharing}
              className={`hidden lg:block relative p-3 sm:p-3.5 rounded-full ${toolbarInteractive} ${
                isSomeoneElseSharing
                  ? "cursor-not-allowed border-gray-200 bg-gray-200 text-gray-400 shadow-none active:scale-100"
                  : isScreenSharing
                    ? "border-primary/80 bg-primary text-white shadow-lg hover:border-primary hover:bg-primary-hover hover:shadow-xl hover:ring-2 hover:ring-primary/25"
                    : `${secondaryButtonClass}`
              }`}
              aria-label={
                isSomeoneElseSharing
                  ? t("controls.aria.screenSharingDisabled")
                  : isScreenSharing
                    ? t("controls.aria.stopSharingScreen")
                    : t("controls.aria.shareScreen")
              }
              aria-pressed={isScreenSharing}
              title={
                isSomeoneElseSharing
                  ? t("controls.aria.someoneElseSharing")
                  : isScreenSharing
                    ? t("controls.aria.stopSharingScreen")
                    : t("controls.aria.shareScreen")
              }
            >
              <Monitor className={iconClass} />
            </button>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-8 bg-gray-300" aria-hidden="true" />

          {/* Collaboration Tools Group */}
          <div className="hidden lg:flex items-center gap-1.5 sm:gap-2">
            {showBreakoutControl && (
              <button
                onClick={isInBreakout ? onReturnToMainMeeting : onToggleBreakouts}
                className={secondaryButtonClass}
                aria-label={isInBreakout ? t("breakoutRooms.toolbarReturnMain") : t("breakoutRooms.toolbarOpen")}
                title={isInBreakout ? t("breakoutRooms.toolbarReturnMain") : t("breakoutRooms.toolbarOpen")}
              >
                <Grid2x2 className={iconClass} />
              </button>
            )}
            {/* Chat toggle */}
            {showChatButton && (
              <button
                onClick={onToggleChat}
                className={secondaryButtonClass}
                aria-label={`${t("controls.aria.toggleChat")}${unreadMessages > 0 ? `, ${t("controls.aria.unreadMessages", { count: unreadMessages })}` : ""}`}
                aria-pressed="false"
                title={t("controls.aria.toggleChat")}
              >
                <MessageCircle className={iconClass} />
                {unreadMessages > 0 && (
                  <span
                    className="absolute top-1.5 ltr:right-1.5 rtl:left-1.5 w-2 h-2 bg-primary rounded-full ring-2 ring-white"
                    aria-label={
                      t("chat.noMessages") === "No messages yet"
                        ? "New messages"
                        : t("controls.aria.unreadMessages", { count: unreadMessages })
                    }
                  />
                )}
              </button>
            )}

            {/* Polls */}
            <button
              onClick={onTogglePolls}
              className={secondaryButtonClass}
              aria-label={t("poll.createPoll")}
              title={t("poll.createPoll")}
            >
              <BarChart3 className={iconClass} />
              {unreadPolls && (
                <span
                  className="absolute top-1.5 ltr:right-1.5 rtl:left-1.5 w-2 h-2 bg-primary rounded-full ring-2 ring-white"
                  aria-label={t("controls.aria.newPoll")}
                />
              )}
            </button>

            {/* Shared Notes */}
            <button
              onClick={onToggleNotes}
              className={secondaryButtonClass}
              aria-label={t("controls.notes")}
              title={t("controls.notes")}
            >
              <FileText className={iconClass} />
              {unreadNotes && (
                <span
                  className="absolute top-1.5 ltr:right-1.5 rtl:left-1.5 w-2 h-2 bg-primary rounded-full ring-2 ring-white"
                  aria-label={t("controls.aria.newNotes")}
                />
              )}
            </button>

            {/* Whiteboard */}
            <button
              onClick={onToggleWhiteboard}
              className={secondaryButtonClass}
              aria-label={t("controls.whiteboard")}
              title={t("controls.whiteboard")}
            >
              <Paintbrush className={iconClass} />
            </button>

            {/* Participants */}
            <button
              onClick={onToggleParticipants}
              className={secondaryButtonClass}
              aria-label={`${t("controls.participants")}${raisedHandsCount > 0 ? `, ${t("controls.aria.handsRaised", { count: raisedHandsCount })}` : ""}`}
              title={t("controls.participants")}
            >
              <Users className={iconClass} />
              {hasUnreadJoinRequests && (
                <span
                  className="absolute top-1.5 ltr:right-1.5 rtl:left-1.5 w-2 h-2 bg-primary rounded-full ring-2 ring-white"
                  aria-label={t("controls.aria.newJoinRequests")}
                />
              )}
              {raisedHandsCount > 0 && (
                <span
                  className="absolute -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white shadow-lg ring-2 ring-white ltr:-right-1 rtl:-left-1"
                  aria-label={t("controls.aria.handsRaised", { count: raisedHandsCount })}
                >
                  {raisedHandsCount > 1 ? raisedHandsCount : "✋"}
                </span>
              )}
            </button>

            {/* Settings */}
            <button
              onClick={onToggleSettings}
              className={secondaryButtonClass}
              aria-label={t("controls.settings")}
              title={t("controls.settings")}
            >
              <Settings className={iconClass} />
            </button>
          </div>

          {/* Mobile Menu Button - Opens quick actions panel */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-3 rounded-full bg-white text-gray-600 shadow-md ${toolbarInteractive} border-gray-200/70 hover:border-primary hover:bg-gray-50 hover:shadow-lg hover:ring-2 hover:ring-primary/15`}
            aria-label={t("controls.aria.moreOptions")}
            title={t("controls.aria.moreOptions")}
          >
            {isMobileMenuOpen ? <X className={iconClass} /> : <MoreHorizontal className={iconClass} />}
          </button>

          {/* Divider */}
          <div className="hidden lg:block w-px h-8 bg-gray-300" aria-hidden="true" />

          {/* Reactions - visible on sm+ screens, hidden when mobile menu is open */}
          <div className={`hidden sm:block ${isMobileMenuOpen ? "lg:block sm:hidden" : ""}`}>
            <Reactions
              isHandRaised={isHandRaised}
              onToggleHandRaise={onToggleHandRaise}
              onSendReaction={onSendReaction}
            />
          </div>
          {/* Divider */}
          <div className="w-px h-8 bg-gray-300" aria-hidden="true" />

          {/* Info / Meeting Details Button */}
          <button
            onClick={onToggleMeetingDetails}
            className={secondaryButtonClass}
            aria-label={t("controls.aria.meetingDetails")}
            title={t("controls.aria.meetingDetails")}
          >
            <Info className={iconClass} />
          </button>

          {/* Call + breakout timers */}
          <div className="flex items-center gap-1.5 sm:gap-2 ltr:ml-0.5 rtl:mr-0.5">
            <CallDurationBadge variant="toolbar" />
            <BreakoutToolbarTimers
              timerRemainingSec={breakoutTimerRemainingSec}
              closeCountdownSec={breakoutCloseCountdownSec}
              onReturnNow={onReturnToMainMeeting}
            />
          </div>

          <div className="hidden h-6 w-px bg-gray-300 sm:block sm:h-8" aria-hidden="true" />

          {/* Leave button */}
          <button
            onClick={onLeave}
            className={`flex cursor-pointer items-center gap-2 rounded-full border border-error/90 bg-error px-4 py-2.5 text-white font-semibold shadow-lg transition-all duration-200 hover:border-white/35 hover:bg-error/90 hover:shadow-xl hover:ring-2 hover:ring-error/25 active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-error focus:ring-offset-2 focus:ring-offset-white sm:px-5 sm:py-3 ltr:ml-0.5 rtl:mr-0.5`}
            aria-label={t("controls.leaveCall")}
            title={t("controls.leaveCall")}
          >
            <PhoneOff className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Quick Actions Panel - Slide up from bottom */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-40 cursor-pointer bg-black/30"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Panel */}
          <div className="lg:hidden fixed bottom-20 left-2 right-2 bg-white rounded-2xl shadow-2xl z-50 p-4 animate-in slide-in-from-bottom-4 duration-200">
            <div className="flex items-center justify-between mb-4 px-1">
              <span className="text-sm font-semibold text-gray-700">{t("controls.more")}</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="-mr-1.5 cursor-pointer rounded-full border border-transparent p-1.5 text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-100 active:scale-95"
                aria-label={t("controls.aria.closeMenu")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {showBreakoutControl && (
                <button
                  onClick={() => {
                    if (isInBreakout) onReturnToMainMeeting();
                    else onToggleBreakouts();
                    setIsMobileMenuOpen(false);
                  }}
                  className={mobileMenuButtonClass}
                >
                  <Grid2x2 className="w-5 h-5" />
                  <span className="text-xs">
                    {isInBreakout ? t("breakoutRooms.toolbarMainRoom") : t("breakoutRooms.toolbarOpen")}
                  </span>
                </button>
              )}
              {showChatButton && (
                <button
                  onClick={() => {
                    onToggleChat();
                    setIsMobileMenuOpen(false);
                  }}
                  className={mobileMenuButtonClass}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-xs">{t("controls.chat")}</span>
                  {unreadMessages > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />}
                </button>
              )}
              <button
                onClick={() => {
                  onToggleParticipants();
                  setIsMobileMenuOpen(false);
                }}
                className={mobileMenuButtonClass}
              >
                <Users className="w-5 h-5" />
                <span className="text-xs">{t("controls.participants")}</span>
                {raisedHandsCount > 0 && <span className="absolute top-1 right-1 text-xs">✋</span>}
              </button>
              <button
                onClick={() => {
                  onTogglePolls();
                  setIsMobileMenuOpen(false);
                }}
                className={mobileMenuButtonClass}
              >
                <BarChart3 className="w-5 h-5" />
                <span className="text-xs">{t("poll.createPoll")}</span>
                {unreadPolls && <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />}
              </button>
              <button
                onClick={() => {
                  onToggleNotes();
                  setIsMobileMenuOpen(false);
                }}
                className={mobileMenuButtonClass}
              >
                <FileText className="w-5 h-5" />
                <span className="text-xs">{t("controls.notes")}</span>
              </button>
              <button
                onClick={() => {
                  onToggleWhiteboard();
                  setIsMobileMenuOpen(false);
                }}
                className={mobileMenuButtonClass}
              >
                <Paintbrush className="w-5 h-5" />
                <span className="text-xs">{t("controls.whiteboard")}</span>
              </button>
              <button
                onClick={() => {
                  onToggleSettings();
                  setIsMobileMenuOpen(false);
                }}
                className={mobileMenuButtonClass}
              >
                <Settings className="w-5 h-5" />
                <span className="text-xs">{t("controls.settings")}</span>
              </button>
              <button
                onClick={() => {
                  onToggleHandRaise();
                  setIsMobileMenuOpen(false);
                }}
                className={`${mobileMenuButtonClass} ${isHandRaised ? "border-amber-400/80 bg-warning/20 text-warning hover:border-amber-500 hover:ring-amber-400/20" : ""}`}
              >
                <span className="text-lg">✋</span>
                <span className="text-xs">{isHandRaised ? t("controls.lowerHand") : t("controls.raiseHand")}</span>
              </button>
            </div>
            {/* Emoji Reactions Row for Mobile */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2 text-center">{t("controls.reactions")}</p>
              <div className="grid grid-cols-8 gap-1">
                {["👍", "👏", "❤️", "😂", "🎉", "🔥", "🤔", "👎"].map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => {
                      onSendReaction(emoji);
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex cursor-pointer items-center justify-center rounded-lg border border-transparent p-1.5 text-xl transition-all hover:border-gray-200 hover:bg-gray-100 active:scale-90"
                    aria-label={`Send ${emoji} reaction`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};
